import { forwardRef, useImperativeHandle, useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';

import { attachDocument, attachDocuments, createReservation, createTransaction, removeReservation } from "../../../../api/Product";
import { useCustomStripe } from "../../../../common/Providers/CustomStripeProvider/UseCustomStripe";
import { useVehicleReservation } from "../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useStoreDetails } from "../../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { API_URL } from "../../../../common/AppConstants";
import { formatDateString } from "../../../../common/Utils";


export type PaymentFormRef = {
  submit: () => void;
};

type PaymentFormProps = {
  onBeforeConfirmPayment?: () => Promise<number | null>;
  onPaymentSuccess?: (args: { reservationId: number | null; paymentIntentId?: string; amount?: number }) => Promise<void> | void;
  returnUrlPath?: string;
  buttonText?: string;
  hideDefaultButton?: boolean;
};

const PaymentForm = forwardRef<PaymentFormRef, PaymentFormProps>(function PaymentForm({ onBeforeConfirmPayment, onPaymentSuccess, returnUrlPath = "/rental/thankyou", buttonText = "Pay Now", hideDefaultButton = false }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { clientSecret } = useCustomStripe();
  const { VehicleReservationItems, VehicleReservationMain } = useVehicleReservation();
  const { storeDetails, showLoader, hideLoader } = useStoreDetails();

  const [isLoading, setIsLoading] = useState(false);

  const showError = (message: string) => {
    enqueueSnackbar(message, {
      variant: "error",
      style: { width: "350px" },
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(),
  }));

  const handleSubmit = async (e?: any) => {
    if (e?.preventDefault) e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    showLoader();
    setIsLoading(true);

    const currentURL = window.location.href;
    const url = new URL(currentURL);
    const protocol = url.protocol;
    const host = url.host;
    const fullHost = protocol + "//" + host;

    // First, validate payment with Stripe BEFORE creating reservation
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: fullHost + returnUrlPath,
      },
      redirect: "if_required",
    });

    if (error) {
      showError(
        error.type === "card_error" || error.type === "validation_error"
          ? (error.message as string)
          : "An unexpected error occurred."
      );

      removeStorageValues();
      hideLoader();
      setIsLoading(false);
      return;
    }

    // Payment successful, now create reservation
    const reservationId = await runBeforeConfirmPayment();

    if (onPaymentSuccess) {
      await onPaymentSuccess({
        reservationId,
        paymentIntentId: paymentIntent.id,
        amount: paymentIntent.amount / 100,
      });
    } else {
      navigate(returnUrlPath);
    }

    hideLoader();
    setIsLoading(false);
  };

  const runBeforeConfirmPayment = async () => {
    return onBeforeConfirmPayment
      ? await onBeforeConfirmPayment()
      : await createOrderRental();
  };

  const createOrderRental = async () => {
    if (!stripe) return null;

    const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret.client_secret);

    if (!paymentIntent) return null;

    const payload = {
      brand_id: storeDetails.brand_id,
      start_date: VehicleReservationMain.pickup ? formatDateString(VehicleReservationMain.pickup) : '',
      end_date: VehicleReservationMain.dropoff ? formatDateString(VehicleReservationMain.dropoff) : '',
      subtotal: VehicleReservationMain.prices.subtotal,
      tax_rate: storeDetails.sales_tax,
      tax_amount: VehicleReservationMain.prices.tax,
      discount_amount: VehicleReservationMain.prices.discount,
      total_price: paymentIntent.amount / 100,
      price_table_id: VehicleReservationMain.price_table_id,
      stage: 2,
      delivery_method: VehicleReservationMain.orderType === 'ride_off' ? 0 : 1,
      address_id: VehicleReservationMain.address_id,
      use_manual: VehicleReservationMain.use_manual,
      manual_address: VehicleReservationMain.manual_address,
      mapbox_id: VehicleReservationMain.mapbox_id,
      usps_address: VehicleReservationMain.usps_address,
      lat: VehicleReservationMain.lat,
      lng: VehicleReservationMain.lng,
      name: VehicleReservationMain.name,
      address: VehicleReservationMain.address,
      address2: VehicleReservationMain.address2,
      city: VehicleReservationMain.city,
      state: VehicleReservationMain.state,
      postal_code: VehicleReservationMain.postal_code,
      email: VehicleReservationMain.email,
      phone_number: VehicleReservationMain.phone_number,
      driver_tip: VehicleReservationMain.driver_tip,
      customer_id: localStorage.getItem('customerId'),
      items: VehicleReservationItems,
      discount_code: VehicleReservationMain.discount_code,
      promo_code: VehicleReservationMain.promo_code,
      note: VehicleReservationMain.note,
      stripe_cus_id: clientSecret.customer,
      preId: localStorage.getItem('preId') || null,
      start_time: VehicleReservationMain.pickup_time,
      end_time: VehicleReservationMain.dropoff_time,
      bodhi_protect_opted_out: VehicleReservationMain.bodhi_protect_opted_out,
      bodhi_protect_price: VehicleReservationMain.bodhi_protect_price,
      delivery_charge: VehicleReservationMain.delivery_charge || 0,
      service_fee: VehicleReservationMain.prices.service_fee,
      pricing_system: 1,
      is_quick: storeDetails.isQuickOrder || storeDetails.isRentalFlow ? true : false,
    };

    // Save reservation
    const response: any = await createReservation(payload);
    const newReservationData = await response.clone().json();

    if (!newReservationData) {
      showError("Reservation failed!");
      return null;
    }

    const reservation = newReservationData.reservation;
    const reservationId = reservation.id;

    // Save transaction
    await createTransaction({
      payment_intent: paymentIntent.id,
      reservation_id: reservationId,
      method: "Stripe",
      amount: paymentIntent.amount / 100,
    });

    // Upload docs
    try {
      await uploadAllDocuments({
        reservation,
        license: VehicleReservationMain.license_file,
        insurance: VehicleReservationMain.insurance_file,
        agreement: VehicleReservationMain.agreement_file,
      });
    } catch (err: any) {
      console.error("Document upload failed:", err);
      enqueueSnackbar(`Some documents failed to upload: ${err.message}`, {
        variant: "warning",
        style: { width: "350px" },
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }

    setStorageValues(reservationId, reservation.order_number);
    return reservationId;
  }

  const uploadAllDocuments = async ({
    reservation,
    license,
    insurance,
    agreement,
  }: {
    reservation: any;
    license?: any; // FileWithMetadata | null
    insurance?: any; // FileWithMetadata | null
    agreement?: any; // FileWithMetadata | null
  }) => {
    try {
      // Upload license if present
      if (license) {
        await uploadMultipleDocuments([license], 'DRIVER_LICENSE', reservation);
      }

      // Upload insurance if present
      if (insurance) {
        await uploadMultipleDocuments([insurance], 'INSURANCE', reservation);
      }

      // Upload agreement (waiver) if present
      if (agreement) {
        await uploadSingleDocument(agreement, 'WAIVER', reservation);
      }

    } catch (err: any) {
      console.error("Document upload failed:", err);
      throw new Error(`Document upload failed: ${err.message}`);
    }
  };

  const uploadMultipleDocuments = async (filesWithMetadata: any[], documentType: string, reservation: any) => {
    const form = new FormData();

    // Extract files and metadata
    const files = filesWithMetadata.map(item => item.file);
    const metadataArray = filesWithMetadata.map(item => item.metadata);

    // Add files to form
    files.forEach(file => {
      form.append("files", file);
    });

    // Add metadata as JSON string
    form.append("metadata", JSON.stringify(metadataArray));

    form.append("document_type", documentType);
    form.append("customer_id", reservation.customer_id ?? "");
    form.append("reservation_id", reservation.id ?? "");

    return new Promise((resolve, reject) => {
      attachDocuments(form, (jsonRes, status, error) => {
        if (status === 200) {
          console.log(`Successfully uploaded ${files.length} ${documentType} documents:`, jsonRes);
          resolve(jsonRes);
        } else {
          console.error(`Failed to upload ${documentType} documents:`, error);
          reject(new Error(`Failed to upload ${documentType}: ${error}`));
        }
      });
    });
  };

  const uploadSingleDocument = async (fileWithMetadata: any, documentType: string, reservation: any) => {
    const form = new FormData();

    // Extract file and metadata
    form.append("file", fileWithMetadata.file);
    form.append("metadata", JSON.stringify(fileWithMetadata.metadata));
    form.append("document_type", documentType);
    form.append("customer_id", reservation.customer_id ?? "");
    form.append("reservation_id", reservation.id ?? "");

    return new Promise((resolve, reject) => {
      attachDocument(form, (jsonRes, status, error) => {
        if (status === 200) {
          console.log(`Successfully uploaded ${documentType}:`, jsonRes);
          resolve(jsonRes);
        } else {
          console.error(`Failed to upload ${documentType}:`, error);
          reject(new Error(`Failed to upload ${documentType}: ${error}`));
        }
      });
    });
  };

  const setStorageValues = (reservationId: number, order_number: string) => {
    localStorage.setItem('_r_id', reservationId.toString());
    localStorage.setItem('_r_order_number', order_number);
    localStorage.setItem('_r_name', VehicleReservationMain.name);
    localStorage.setItem('_r_email', VehicleReservationMain.email);
    localStorage.setItem('_r_phone', VehicleReservationMain.phone_number);

    // Format pickup date and time
    const pickupDate = dayjs(VehicleReservationMain.pickup).format('MMMM DD, YYYY');
    const pickupTime = VehicleReservationMain.pickup_time || '';
    localStorage.setItem('_r_pickup', `${pickupDate}${pickupTime ? ' at ' + pickupTime : ''}`);

    // Format dropoff date and time
    const dropoffDate = dayjs(VehicleReservationMain.dropoff).format('MMMM DD, YYYY');
    const dropoffTime = VehicleReservationMain.dropoff_time || '';
    localStorage.setItem('_r_dropoff', `${dropoffDate}${dropoffTime ? ' at ' + dropoffTime : ''}`);

    localStorage.setItem('_r_logo_url', encodeURI(API_URL + storeDetails.logo_url));
    localStorage.setItem('_r_store_name', storeDetails.store_name);
    
    // Save additional drivers if they exist
    if (VehicleReservationMain.additional_drivers && VehicleReservationMain.additional_drivers.length > 0) {
      localStorage.setItem('_r_additional_drivers', JSON.stringify(VehicleReservationMain.additional_drivers));
    }
  }

  const removeStorageValues = () => {
    localStorage.removeItem('_r_id');
    localStorage.removeItem('_r_order_number');
    localStorage.removeItem('_r_name');
    localStorage.removeItem('_r_email');
    localStorage.removeItem('_r_phone');
    localStorage.removeItem('_r_pickup');
    localStorage.removeItem('_r_dropoff');
    localStorage.removeItem('_r_logo_url');
    localStorage.removeItem('_r_store_name');
  }

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      {!hideDefaultButton && (
        <LoadingButton
          variant="contained"
          disabled={isLoading || !stripe || !elements}
          loading={isLoading}
          sx={{ mt: '20px', float: 'right', textTransform: 'none' }}
          onClick={handleSubmit}>
          {buttonText}
        </LoadingButton>
      )}
    </form>
  );
});

export default PaymentForm;